import React, { useRef, useEffect, memo } from "react";
import * as d3 from "d3";
import { DonutChartDataItemProps } from "./ChartTypes";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

interface DonutChartProps {
  parentid?: string;
  data: DonutChartDataItemProps[];
  static_color?: boolean;
  valuetype?: string;
  handleClick?: (label: string, e: any, data?: any) => void;
  roundedCorner?: boolean;
  groupSmall?: boolean;
  legendType?: string;
  showTotal?: boolean;
  title?: string;
  titlePosition?: string;
  isPieChart?: boolean;
  enableAnimation?: boolean;
}

const DonutChart: React.FC<DonutChartProps> = memo(
  ({
    data,
    static_color = false,
    valuetype = "count",
    handleClick,
    roundedCorner = false,
    groupSmall = true,
    legendType = "line",
    showTotal = true,
    title,
    titlePosition = "top",
    isPieChart = false,
    enableAnimation = true,
  }) => {
    // const scale = window.innerWidth / 1920;
    const [labelType, setLabelType] = React.useState(legendType);
    const svgRef = useRef<SVGSVGElement>(null);
    const legendRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [newdata, setNewData] =
      React.useState<DonutChartDataItemProps[]>(data);
    let hoverArc: any;
    let arc: any;
    function shortNumber(value: number) {
      if (valuetype == "count") {
        if (value > 999999) {
          return (value / 1000000).toFixed(2) + "M";
        } else if (value > 999) {
          return (value / 1000).toFixed(2) + "K";
        } else {
          if (Number.isInteger(value)) {
            return value;
          } else {
            return value.toFixed(2);
          }
        }
      } else if (valuetype == "bandwidth") {
        if (value > 1000 * 1000 * 1000 * 1000) {
          return (value / (1000 * 1000 * 1000 * 1000)).toFixed(2) + "TB";
        } else if (value > 1000 * 1000 * 1000) {
          return (value / (1000 * 1000 * 1000)).toFixed(2) + "GB";
        } else if (value > 1000 * 1000) {
          return (value / (1000 * 1000)).toFixed(2) + "MB";
        } else {
          return value + "KB";
        }
      }
      return value.toFixed(2);
    }
    data = data.sort((a, b) => b.value - a.value);
    data = data.filter((d) => d.label.trim() !== "" && d.value > 0);

    const combinedData: any = [];

    data.forEach((item: any) => {
      const exist = combinedData.find((d: any) => d.label === item.label);
      if (exist) {
        exist.value += item.value;
        if (!exist.color && item.color) {
          exist.color = item.color;
        }
      } else {
        combinedData.push({ ...item });
      }
      // if (combinedData[item.label]) {
      //   combinedData[item.label] += item.value;
      // } else {
      //   combinedData[item.label] = item.value;
      // }
    });

    // data = Object.entries(combinedData).map(([label, value]) => ({
    //   label,
    //   value,
    // }));
    data = combinedData;

    const color = d3
      .scaleOrdinal<string>()
      .domain(data.map((d) => d.label))
      .range(d3.schemeCategory10);

    const getSegmentColor = (label: string) => {
      if (static_color) {
        const match = data.find((item) => item.label === label);
        if (match?.color) {
          return match.color;
        }
      }
      return color(label);
    };

    function drawChart() {
      svgRef.current?.classList.remove("overflow-visible");
      d3.select(svgRef.current).selectAll("*").remove();

      const rect = svgRef.current?.getBoundingClientRect();
      const width = rect ? rect.width : 0;
      const height = rect ? rect.height : 0;
      const svg = d3.select(svgRef.current);

      const rectParent = legendRef.current?.getBoundingClientRect();
      const widthParent = rectParent ? rectParent.width : 0;
      const heightParent = rectParent ? rectParent.height : 0;

      const aspectRatio = widthParent / heightParent;
      legendType =
        labelType == "line" && aspectRatio < 1.3 ? "list" : labelType;

      setLabelType(legendType);

      svg.attr("width", width).attr("height", height);
      const radius =
        Math.min(width, height) / (labelType == "line" ? 2.5 : 2.2);

      arc = d3
        .arc<d3.PieArcDatum<{ label: string; value: number }>>()
        .innerRadius(
          radius * (isPieChart ? 0 : labelType == "list" ? 0.8 : 0.6),
        )
        .outerRadius(radius * (labelType == "list" ? 0.9 : 0.7))
        .cornerRadius(2);

      hoverArc = d3
        .arc<d3.PieArcDatum<{ label: string; value: number }>>()
        .innerRadius(
          radius * (isPieChart ? 0 : labelType == "list" ? 0.8 : 0.6),
        )
        .outerRadius(radius * (labelType == "list" ? 0.95 : 0.75))
        .cornerRadius(2);

      if (roundedCorner) {
        arc.cornerRadius(100);
      }

      const arc1 = d3
        .arc<d3.PieArcDatum<{ label: string; value: number }>>()
        .innerRadius(radius * 0.75)
        .outerRadius(radius * 0.8);

      const outerArc = d3
        .arc<d3.PieArcDatum<{ label: string; value: number }>>()
        .innerRadius(radius * 0.85)
        .outerRadius(radius * 0.85);

      const pie = d3
        .pie<DonutChartDataItemProps>()
        .value((d) => d.value)
        .sort(null);

      let sum = 0;
      data.forEach((d) => {
        sum += parseFloat(d.value.toString());
      });
      if (valuetype == "percentage") {
        sum = Math.min(sum, 100);
      }

      const newdataTemp: DonutChartDataItemProps[] = [];
      let otherdata = 0;
      let other_data_count = 0;
      let other_single_data: DonutChartDataItemProps = {
        label: "",
        value: 0,
        percentage: 0,
      };
      for (const d of data) {
        if (d.value / sum < (groupSmall ? 0.01 : 0.0)) {
          otherdata += parseFloat(d.value.toString());
          other_data_count += 1;
          other_single_data = d;
        } else {
          if (valuetype == "percentage") {
            d.percentage = d.value;
          } else {
            d.percentage = parseFloat(((d.value / sum) * 100).toFixed(2));
          }
          newdataTemp.push(d);
        }
      }

      if (other_data_count == 1) {
        if (valuetype == "count") {
          other_single_data.percentage = parseFloat(
            ((other_single_data.value / sum) * 100).toFixed(2),
          );
        } else {
          other_single_data.percentage = other_single_data.value;
        }
        newdataTemp.push(other_single_data);
      }

      if (otherdata > 0 && other_data_count > 1) {
        if (valuetype == "percentage") {
          newdataTemp.push({
            label: "Others",
            value: parseFloat(otherdata.toFixed(2)),
            percentage: parseFloat(otherdata.toFixed(2)),
          });
        } else {
          newdataTemp.push({
            label: "Others",
            value: parseFloat(otherdata.toFixed(2)),
            percentage: parseFloat(((otherdata / sum) * 100).toFixed(2)),
          });
        }
      }

      if (newdataTemp.length != newdata.length) {
        setNewData(newdataTemp);
      }
      const arcs: any = pie(newdataTemp);

      svg.selectAll("*").remove();

      let titleGapTop = 0;
      if (title && titlePosition == "top") {
        titleGapTop = 50;
      }

      const groups = svg
        .append("g")
        .attr(
          "transform",
          `translate(${width / 2},${height / 2 + titleGapTop})`,
        );

      if (title) {
        let titleY = 50;
        if (titlePosition == "bottom") {
          titleY = height - 10;
        }

        svg
          .append("text")
          .text(title)
          .attr("x", width / 2)
          .attr("y", titleY)
          .style("text-anchor", "middle")
          .style("font-size", "0.8rem")
          .style("fill", "var(--muted-foreground)");
      }

      const arcPaths = groups
        .selectAll("path")
        .data(arcs)
        .enter()
        .append("path")
        .attr("fill", (d: any) => getSegmentColor(d.data.label))
        .attr("data-pathvalue", (d: any) => d.data.label)
        .attr("data-value", (d: any) => d.data.value)
        // .attr("class", "viewData")
        .attr("d", arc);

      if (enableAnimation) {
        arcPaths
          .style("opacity", 0)
          .style("transition", "all ease-out 150ms")
          .style("transform", "scale(3)")
          .transition()
          .duration(1000)
          .delay(function (_d, i: any) {
            return i * 100;
          })
          .style("transform", "scale(1)")
          .style("opacity", 1);
      }

      arcPaths
        .on("mouseover", function (_, i: any) {
          const label: any = svg.select(
            `.labeltext[data-textLabel="${i.data.label}"]`,
          );
          if (!label.empty()) {
            const rect = label.node()?.getBoundingClientRect();
            if (rect) {
              const tooltipDiv: any = d3.select(tooltipRef.current);
              tooltipDiv.transition().duration(200).style("opacity", "1");

              const text = label.attr("data-text");
              tooltipDiv.node().innerHTML = text;

              // Wait for DOM update to get correct width
              requestAnimationFrame(() => {
                const tooltipWidth = tooltipDiv.node()?.offsetWidth || 150;
                const tooltipHeight = tooltipDiv.node()?.offsetHeight || 40;

                let left = rect.x;
                let top = rect.y - tooltipHeight - 8; // space above

                // Prevent going off right edge
                if (left + tooltipWidth > window.innerWidth - 10) {
                  left = window.innerWidth - tooltipWidth - 10;
                }

                // Prevent going off left edge
                if (left < 10) {
                  left = 10;
                }

                // Prevent tooltip from going above the top
                if (top < 10) {
                  top = rect.y + rect.height + 8; // show below instead
                }

                // Apply final positions
                tooltipDiv
                  .style("left", left + "px")
                  .style("top", top + "px")
                  .style("right", "auto");
              });
            }
          }
          d3.select(this).attr("d", hoverArc);

          d3.select(texts.nodes()[i.index])
            .transition()
            .duration(500)
            .style("font-weight", "bold")
            .attr("x", 0)
            .attr("y", 0 + 10)
            .style("font-size", "1rem")
            .style("opacity", 1);

          totalText
            ?.transition()
            .duration(500)
            .style("transform", "scale(0)")
            .style("opacity", 0);
        })
        .on("mouseout", function (_, i: any) {
          const tooltipDiv: any = d3.select(tooltipRef.current);
          tooltipDiv.transition().duration(200).style("opacity", "0");

          d3.select(this).attr("d", arc);

          d3.select(texts.nodes()[i.index])
            .transition()
            .duration(500)
            .attr("x", function (d) {
              return arc.centroid(
                d as d3.PieArcDatum<DonutChartDataItemProps>,
              )[0];
            })
            .attr("y", function (d) {
              return arc.centroid(
                d as d3.PieArcDatum<DonutChartDataItemProps>,
              )[1];
            })
            .style("font-size", "0.7rem")
            .style("opacity", 0);

          totalText
            ?.transition()
            .duration(500)
            .style("transform", "scale(1)")
            .style("opacity", 1);
        })
        .on("click", function (d, i: any) {
          d.preventDefault();
          const value = i.data.label;
          if (handleClick) {
            handleClick(value, d, i.data);
          }
        });

      const texts = groups
        .selectAll("allSlices")
        .data(arcs)
        .enter()
        .append("text")
        .style("fill", "var(--foreground)")
        .style("font-size", "2rem")
        .attr("data-textValue", (d: any) => d.data.label)
        .style("opacity", 0)
        .attr("x", function (d) {
          return arc.centroid(d)[0];
        })
        .attr("y", function (d) {
          return arc.centroid(d)[1];
        })
        .style("background-color", "white")
        .attr("text-anchor", "middle")
        .text(
          (d: any) =>
            shortNumber(parseFloat(d.data.value.toString())) +
            (valuetype == "percentage" ? " %" : ""),
        )
        .style("cursor", "pointer")
        .style("pointer-events", "none");

      let totalText: any = null;
      if (showTotal) {
        const sumtext =
          shortNumber(sum) + (valuetype == "percentage" ? " %" : "");
        totalText = groups
          .append("text")
          .attr("x", 0)
          .attr("y", 10)
          .attr("text-anchor", "middle")
          .style("font-size", "1rem")
          .style("fill", "var(--foreground)")
          .style("font-weight", "bold")
          .text(sumtext)
          .attr("id", "donutTotalText")
          .style("transform", "scale(0)")
          .style("opacity", 0);

        totalText
          .transition()
          .duration(1000)
          .delay(data.length * 100)
          .style("transform", "scale(1)")
          .style("opacity", 1);
      }

      if (labelType == "line") {
        const prevR: any = {};
        const mindist = 20;
        arcs.forEach((d: any) => {
          const pos = outerArc.centroid(d);
          d["yPos"] = pos[1];
        });
        arcs.sort(
          (a: any, b: any) => Math.abs(a["yPos"]) - Math.abs(b["yPos"]),
        );

        arcs.forEach((d: any) => {
          const pos = outerArc.centroid(d);

          const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
          pos[0] = radius * 0.95 * (midangle < Math.PI ? 1 : -1);

          const posA = arc1.centroid(d);
          const posB = outerArc.centroid(d);
          const posC = outerArc.centroid(d);

          posC[0] = radius * 0.9 * (midangle < Math.PI ? 1 : -1);
          posC[1] = pos[1];
          posB[1] = pos[1];

          const posKey = pos[0].toFixed();

          if (posKey in prevR) {
            const r = prevR[posKey];
            if (pos[1] < 0) {
              const diff = Math.abs(pos[1] - r["min"]);
              if (diff < mindist || pos[1] > r["min"]) {
                pos[1] = r["min"] - mindist;
                posC[1] = r["min"] - mindist;
                posB[1] = r["min"] - mindist;
              }
            } else {
              const diff = Math.abs(pos[1] - r["max"]);
              if (diff < mindist || pos[1] < r["max"]) {
                pos[1] = r["max"] + mindist;
                posC[1] = r["max"] + mindist;
                posB[1] = r["max"] + mindist;
              }
            }
          }

          //Label
          const labText = groups
            .append("text")
            .attr("class", "labeltext")
            .style("font-size", "0.7rem")
            .style("fill", "var(--foreground)")
            .style("opacity", 0)
            .style("cursor", "pointer")
            .attr("data-textLabel", () => d.data.label)
            .attr(
              "data-text",
              () =>
                d.data.label +
                " (" +
                shortNumber(d.data.percentage || 0) +
                "%)",
            )
            .text(function () {
              const labeltext = d.data.label;
              return (
                labeltext + " (" + shortNumber(d.data.percentage || 0) + "%)"
              );
            })
            .attr("transform", function () {
              return "translate(" + pos + ")";
            })
            .style("text-anchor", function () {
              const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
              return midangle < Math.PI ? "start" : "end";
            })
            .attr("text-side", function () {
              const midangle = d.startAngle + (d.endAngle - d.startAngle) / 2;
              return midangle < Math.PI ? "right" : "left";
            });

          let fitSize = true;
          let newName = d.data.label;
          while (fitSize) {
            const labTextRect = labText.node()?.getBBox();
            if (pos[0] > 0 && labTextRect) {
              const endPoint = pos[0] + labTextRect.width + 10;
              if (endPoint > width / 2 && newName.length > 0) {
                newName = newName.slice(0, -1);
                labText.text(
                  newName +
                    "... (" +
                    shortNumber(d.data.percentage || 0) +
                    "%)",
                );
              } else {
                fitSize = false;
              }
            } else if (pos[0] <= 0 && labTextRect) {
              const endPoint = pos[0] - labTextRect.width - 10;
              if (endPoint < -width / 2 && newName.length > 0) {
                newName = newName.slice(0, -1);
                labText.text(
                  newName +
                    "... (" +
                    shortNumber(d.data.percentage || 0) +
                    "%)",
                );
              } else {
                fitSize = false;
              }
            }
          }

          labText
            .on("mouseover", function (e: any) {
              const label = e.target.getAttribute("data-textLabel");
              const path: any = svg.select(`path[data-pathvalue="${label}"]`);
              if (!path.empty()) {
                path.node()?.dispatchEvent(new MouseEvent("mouseover"));
              }
            })
            .on("mouseout", function (e: any) {
              const label = e.target.getAttribute("data-textLabel");
              const path: any = svg.select(`path[data-pathvalue="${label}"]`);
              if (!path.empty()) {
                path.node()?.dispatchEvent(new MouseEvent("mouseout"));
              }
            })
            .on("click", function (e) {
              const value = d.data.label;
              if (handleClick) {
                handleClick(value, e, d.data);
              }
            })
            .transition()
            .duration(500)
            .delay(function (_d, i) {
              return i * 100 + 1000;
            })
            .style("opacity", 1);

          //Line
          groups
            .append("polyline")
            .attr("stroke", "#a5a6a7")
            .style("fill", "none")
            .attr("stroke-width", 1)
            .style("opacity", 0)
            .style("transition", "all ease-out 150ms")
            .attr("points", function () {
              const points = [posA, posB, posC];
              return points.map((point) => point.join(",")).join(" ");
            })
            .transition()
            .duration(500)
            .delay(function (_d, i) {
              return i * 100 + 1000;
            })
            .style("opacity", 1);

          if (posKey in prevR) {
            prevR[posKey]["max"] = Math.max(prevR[posKey]["max"], pos[1]);
            prevR[posKey]["min"] = Math.min(prevR[posKey]["min"], pos[1]);
          } else {
            prevR[posKey] = {
              max: Math.max(pos[1], 0),
              min: Math.min(pos[1], 0),
            };
          }

          if (Math.abs(pos[1]) > height / 2 - 10) {
            setLabelType("list");
          }
        });
      }
      setTimeout(() => {
        svgRef.current?.classList.add("overflow-visible");
      }, 2000);
    }

    useEffect(() => {
      drawChart();
    }, [labelType]);

    useEffect(() => {
      if (!svgRef.current) return;
      drawChart();
    }, [data]);

    function legendHover(label: string) {
      d3.select(svgRef.current)
        .select(`path[data-pathvalue="${label}"]`)
        .attr("d", hoverArc);

      d3.select(svgRef.current)
        .select(`text[data-textValue="${label}"]`)
        .transition()
        .duration(500)
        .style("font-weight", "bold")
        .attr("x", 0)
        .attr("y", 0 + 10)
        .style("font-size", "1rem")
        .style("opacity", 1);

      d3.select(svgRef.current)
        .select("#donutTotalText")
        .transition()
        .duration(500)
        .style("transform", "scale(0)");
    }

    function legendMouseOut(label: string) {
      d3.select(svgRef.current)
        .select(`path[data-pathvalue="${label}"]`)
        .attr("d", arc);

      d3.select(svgRef.current)
        .select(`text[data-textValue="${label}"]`)
        .transition()
        .duration(500)
        .attr("x", function (d) {
          return arc.centroid(d as d3.PieArcDatum<DonutChartDataItemProps>)[0];
        })
        .attr("y", function (d) {
          return arc.centroid(d as d3.PieArcDatum<DonutChartDataItemProps>)[1];
        })
        .style("font-size", "0.7rem")
        .style("opacity", 0);

      d3.select(svgRef.current)
        .select("#donutTotalText")
        .transition()
        .duration(500)
        .style("transform", "scale(1)");
    }

    return (
      <div ref={legendRef} className="flex h-full w-full text-xs">
        <div
          ref={tooltipRef}
          className="fixed top-0 left-0 py-1 px-2 rounded-md bg-background max-w-[22rem] whitespace-normal break-words opacity-0 pointer-events-none z-modal-level-3"
        ></div>
        <svg className="flex-1 h-full" ref={svgRef}></svg>
        {labelType == "list" && (
          <div className="flex overflow-y-auto max-w-[40%] flex-col">
            <div className="flex w-full items-center my-auto">
              <div className="w-full flex flex-col gap-2 items-start relative pe-4">
                {newdata.map((item: any, index: number) => (
                  <TooltipProvider key={index} delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          key={index}
                          className="w-full text-start text-xs cursor-pointer overflow-x-hidden whitespace-nowrap text-ellipsis"
                          onMouseOut={() => legendMouseOut(item.label)}
                          onMouseOver={() => legendHover(item.label)}
                          onClick={(e) => {
                            if (handleClick) {
                              handleClick(item.label, e, item);
                            }
                          }}
                        >
                          <div
                            className="w-3 h-3 mr-2 rounded-sm inline-block"
                            style={{
                              backgroundColor: getSegmentColor(item.label),
                            }}
                          ></div>
                          <span>{item.label}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <span>{item.label}</span>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
  arePropsEqual,
);

function arePropsEqual(prevProps: any, nextProps: any) {
  if (prevProps.static_color !== nextProps.static_color) {
    return false;
  }

  if (Array.isArray(prevProps.data) && Array.isArray(nextProps.data)) {
    if (prevProps.data.length !== nextProps.data.length) {
      return false;
    }

    for (let i = 0; i < prevProps.data.length; i++) {
      const label = prevProps.data[i].label;
      if (nextProps.data.some((d: any) => d.label === label) === false) {
        return false;
      } else {
        // Check if the label exists in nextProps.data
        const nextData = nextProps.data.find((d: any) => d.label === label);
        const value = nextData.value;
        if (value != prevProps.data[i].value) {
          return false;
        }
        if (nextData.color != prevProps.data[i].color) {
          return false;
        }
      }
    }

    return true;
  }

  // Fallback to strict equality check if data is not an array
  return prevProps.data === nextProps.data;
}

DonutChart.displayName = "DonutChart";

export default DonutChart;
